import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import styles from "./Web.module.css";

import { useNavigate } from "react-router-dom";


/*
=========================================================
COOKIE CONSENT STORAGE
=========================================================
*/

const COOKIE_CONSENT_KEY =
  "brobizee_cookie_consent";


/*
=========================================================
DEFAULT COOKIE SETTINGS
=========================================================
*/

const DEFAULT_COOKIE_SETTINGS = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};


/*
=========================================================
READ SAVED COOKIE CONSENT
=========================================================
*/

const getSavedCookieConsent = () => {

  try {

    const saved =
      localStorage.getItem(
        COOKIE_CONSENT_KEY
      );


    if (!saved) {

      return null;

    }


    const parsed =
      JSON.parse(saved);


    if (
      !parsed ||
      typeof parsed !== "object"
    ) {

      return null;

    }


    return parsed;

  } catch (error) {

    console.error(
      "BroBizee cookie consent read error:",
      error
    );

    return null;

  }

};


/*
=========================================================
SAVE COOKIE CONSENT
=========================================================
*/

const saveCookieConsent = (
  settings,
  action = "custom"
) => {

  try {

    const consentData = {

      ...settings,

      action,

      consentGiven: true,

      timestamp:
        new Date().toISOString(),

    };


    localStorage.setItem(
      COOKIE_CONSENT_KEY,
      JSON.stringify(
        consentData
      )
    );


    /*
      Dispatch custom event.

      Useful if another component
      wants to react to cookie changes.
    */

    window.dispatchEvent(
      new CustomEvent(
        "brobizee-cookie-consent",
        {
          detail: consentData,
        }
      )
    );


    return true;

  } catch (error) {

    console.error(
      "BroBizee cookie consent save error:",
      error
    );

    return false;

  }

};


export default function Web() {

  const navigate =
    useNavigate();


  /* =====================================================
     STATES
  ====================================================== */

  const [
    currentSlide,
    setCurrentSlide
  ] = useState(0);


  const [
    mousePosition,
    setMousePosition
  ] = useState({
    x: -500,
    y: -500,
  });


  const cardsRef =
    useRef([]);


  /* =====================================================
     COOKIE STATES
  ====================================================== */

  const [
    showCookiePopup,
    setShowCookiePopup
  ] = useState(false);


  const [
    showCookieSettings,
    setShowCookieSettings
  ] = useState(false);


  const [
    cookieSettings,
    setCookieSettings
  ] = useState(
    DEFAULT_COOKIE_SETTINGS
  );


  /* =====================================================
     ANDROID DOWNLOAD STATES
  ====================================================== */

  const [
    showDownloadPopup,
    setShowDownloadPopup
  ] = useState(false);


  const [
    downloadProgress,
    setDownloadProgress
  ] = useState(0);


  const [
    downloadStatus,
    setDownloadStatus
  ] = useState("idle");
  // idle | downloading | completed | error | cancelled


  const downloadAbortRef =
    useRef(null);


  /* =====================================================
     CHECK COOKIE CONSENT
  ====================================================== */

  useEffect(() => {

    const savedConsent =
      getSavedCookieConsent();


    /*
      If consent already exists,
      don't show popup again.
    */

    if (savedConsent) {

      setShowCookiePopup(false);


      setCookieSettings({

        necessary:
          savedConsent.necessary !== false,

        preferences:
          savedConsent.preferences === true,

        analytics:
          savedConsent.analytics === true,

        marketing:
          savedConsent.marketing === true,

      });


      return;

    }


    /*
      New visitor:
      show cookie popup.
    */

    setShowCookiePopup(true);

  }, []);


  /* =====================================================
     ACCEPT ALL COOKIES
  ====================================================== */

  const handleAcceptAllCookies = () => {

    const settings = {

      necessary: true,

      preferences: true,

      analytics: true,

      marketing: true,

    };


    saveCookieConsent(
      settings,
      "accept_all"
    );


    setCookieSettings(
      settings
    );

    setShowCookiePopup(false);

    setShowCookieSettings(false);

  };


  /* =====================================================
     REJECT OPTIONAL COOKIES
  ====================================================== */

  const handleRejectOptional = () => {

    const settings = {

      necessary: true,

      preferences: false,

      analytics: false,

      marketing: false,

    };


    saveCookieConsent(
      settings,
      "reject_optional"
    );


    setCookieSettings(
      settings
    );

    setShowCookiePopup(false);

    setShowCookieSettings(false);

  };


  /* =====================================================
     SAVE CUSTOM COOKIE SETTINGS
  ====================================================== */

  const handleSaveCookieSettings = () => {

    const settings = {

      necessary: true,

      preferences:
        cookieSettings.preferences === true,

      analytics:
        cookieSettings.analytics === true,

      marketing:
        cookieSettings.marketing === true,

    };


    saveCookieConsent(
      settings,
      "custom"
    );


    setCookieSettings(
      settings
    );

    setShowCookiePopup(false);

    setShowCookieSettings(false);

  };


  /* =====================================================
     COOKIE TOGGLE
  ====================================================== */

  const handleCookieToggle = (
    type
  ) => {

    /*
      Necessary cookies cannot
      be disabled.
    */

    if (type === "necessary") {

      return;

    }


    setCookieSettings(
      (previous) => ({

        ...previous,

        [type]:
          !previous[type],

      })
    );

  };


  /* =====================================================
     SLIDER DATA
  ====================================================== */

  const slides = [

    {

      image:
        "/images/slide1.png",

      badge:
        "Premium Platform",

      title: (

        <>

          Find Trusted

          <br />

          Home Services

        </>

      ),

      description:
        "Experience India's smartest home service platform with professional experts available anytime.",

      showButtons:
        true,

      showGlassCard:
        true,

    },


    {

      image:
        "/images/slide2.png",

      badge:
        "Fast Booking",

      title: (

        <>

          Professional

          <br />

          Experts

        </>

      ),

      description:
        "196+ Services across India with live tracking.",

      showButtons:
        false,

      showGlassCard:
        false,

    },


    {

      image:
        "/images/slide3.png",

      badge:
        "Modern Experience",

      title: (

        <>

          Future of

          <br />

          Local Services

        </>

      ),

      description:
        "",

      showButtons:
        false,

      showGlassCard:
        false,

    },

  ];


  /* =====================================================
     MOUSE GLOW
  ====================================================== */

  const handleMouseMove = (
    event
  ) => {

    setMousePosition({

      x:
        event.clientX,

      y:
        event.clientY,

    });

  };


  /* =====================================================
     AUTO SLIDER
  ====================================================== */

  useEffect(() => {

    const interval =
      setInterval(() => {

        setCurrentSlide(
          (previous) => {

            if (
              previous >=
              slides.length - 1
            ) {

              return 0;

            }

            return previous + 1;

          }
        );

      }, 5000);


    return () =>
      clearInterval(interval);

  }, [slides.length]);


  /* =====================================================
     NEXT SLIDE
  ====================================================== */

  const nextSlide = () => {

    setCurrentSlide(
      (previous) => {

        if (
          previous >=
          slides.length - 1
        ) {

          return 0;

        }

        return previous + 1;

      }
    );

  };


  /* =====================================================
     PREVIOUS SLIDE
  ====================================================== */

  const previousSlide = () => {

    setCurrentSlide(
      (previous) => {

        if (previous <= 0) {

          return (
            slides.length - 1
          );

        }

        return previous - 1;

      }
    );

  };


  /* =====================================================
     CARD SCROLL ANIMATION
  ====================================================== */

  useEffect(() => {

    const observer =
      new IntersectionObserver(

        (entries) => {

          entries.forEach(
            (entry) => {

              if (
                entry.isIntersecting
              ) {

                entry.target.classList.add(
                  styles.cardVisible
                );


                observer.unobserve(
                  entry.target
                );

              }

            }
          );

        },

        {
          threshold: 0.15,
        }

      );


    cardsRef.current.forEach(
      (card) => {

        if (card) {

          observer.observe(
            card
          );

        }

      }
    );


    return () => {

      cardsRef.current.forEach(
        (card) => {

          if (card) {

            observer.unobserve(
              card
            );

          }

        }
      );

    };

  }, []);


  /* =====================================================
     GET STARTED
  ====================================================== */

  const handleGetStarted = () => {

    navigate("/slider");

  };


  /* =====================================================
     DOWNLOAD IOS
  ====================================================== */

  const handleIosDownload = (
    event
  ) => {

    event.preventDefault();

    console.log(
      "iOS APK clicked"
    );

  };


  /* =====================================================
     DOWNLOAD ANDROID APK (WITH PROGRESS)
  ====================================================== */

  const handleAndroidDownload = async (event) => {

    event.preventDefault();

    /* Reset states */
    setDownloadProgress(0);

    setDownloadStatus("downloading");

    setShowDownloadPopup(true);

    const controller =
      new AbortController();

    downloadAbortRef.current =
      controller;

    try {

      /*
        👉 Yahan apna actual APK URL daalein
        Example: "/BroBizee.apk"
      */

      const apkUrl =
        "/BroBizee.apk";


      const response =
        await fetch(apkUrl, {
          signal:
            controller.signal,
        });


      if (!response.ok) {

        throw new Error(
          "Download failed"
        );

      }


      const contentLength =
        response.headers.get(
          "Content-Length"
        );


      /* Agar Content-Length na mile
         to total 0 maan lo */

      const total =
        contentLength
          ? parseInt(
              contentLength,
              10
            )
          : 0;


      const reader =
        response.body.getReader();

      const chunks = [];

      let received = 0;


      while (true) {

        const { done, value } =
          await reader.read();


        if (done) break;


        chunks.push(value);

        received += value.length;


        if (total > 0) {

          const percent =
            Math.min(
              100,
              Math.round(
                (received / total) *
                  100
              )
            );

          setDownloadProgress(
            percent
          );

        } else {

          /* Agar total size unknown hai
             to fake smooth progress */

          setDownloadProgress(
            (prev) =>
              prev < 90
                ? prev + 2
                : 95
          );

        }

      }


      /* Blob banake download trigger karo */

      const blob = new Blob(
        chunks,
        {
          type:
            "application/vnd.android.package-archive",
        }
      );


      const blobUrl =
        URL.createObjectURL(
          blob
        );


      const link =
        document.createElement(
          "a"
        );

      link.href = blobUrl;

      link.download =
        "BroBizee.apk";

      document.body.appendChild(
        link
      );

      link.click();

      document.body.removeChild(
        link
      );


      URL.revokeObjectURL(
        blobUrl
      );


      setDownloadProgress(100);

      setDownloadStatus(
        "completed"
      );


      /* 2.5 sec baad popup auto close */

      setTimeout(() => {

        setShowDownloadPopup(false);

        setDownloadStatus("idle");

        setDownloadProgress(0);

      }, 2500);

    } catch (error) {

      if (
        error.name ===
        "AbortError"
      ) {

        setDownloadStatus(
          "cancelled"
        );

      } else {

        console.error(
          "Android APK download error:",
          error
        );

        setDownloadStatus(
          "error"
        );

      }


      setTimeout(() => {

        setShowDownloadPopup(false);

        setDownloadStatus("idle");

        setDownloadProgress(0);

      }, 2500);

    } finally {

      downloadAbortRef.current =
        null;

    }

  };


  /* =====================================================
     CANCEL DOWNLOAD
  ====================================================== */

  const handleCancelDownload = () => {

    if (downloadAbortRef.current) {

      downloadAbortRef.current.abort();

    }

    setShowDownloadPopup(false);

    setDownloadStatus("idle");

    setDownloadProgress(0);

  };


  /* =====================================================
     BOOK SERVICE
  ====================================================== */

  const handleBookService = (
    event
  ) => {

    event.preventDefault();

    console.log(
      "Book Service clicked"
    );

  };


  /* =====================================================
     EXPLORE
  ====================================================== */

  const handleExplore = (
    event
  ) => {

    event.preventDefault();

    console.log(
      "Explore clicked"
    );

  };


  /* =====================================================
     RENDER
  ====================================================== */

  return (

    <div
      className={styles.webPage}
      onMouseMove={
        handleMouseMove
      }
    >

      {/* =================================================
          MOUSE GLOW
      ================================================== */}

      <div
        className={styles.mouse}
        style={{
          left:
            `${mousePosition.x}px`,
          top:
            `${mousePosition.y}px`,
        }}
      />


      {/* =================================================
          BACKGROUND BLURS
      ================================================== */}

      <div
        className={
          `${styles.blur} ${styles.blur1}`
        }
      />

      <div
        className={
          `${styles.blur} ${styles.blur2}`
        }
      />


      {/* =================================================
          NAVBAR
      ================================================== */}

      <nav
        className={styles.nav}
      >

        <div
          className={styles.logo}
        >

          <img
            src="/images/logo.png"
            alt="BroBizee Logo"
          />

        </div>


        <button
          type="button"
          className={
            styles.getStarted
          }
          onClick={
            handleGetStarted
          }
        >
          Get Started
        </button>

      </nav>


      {/* =================================================
          MAIN HERO
      ================================================== */}

      <section
        className={styles.hero}
      >

        <div
          className={
            styles.heroLeft
          }
        >

          <h1>

            Next Generation

            <span>
              Digital Experience
            </span>

          </h1>


          <p>

            Build the future with
            premium technology,
            AI-powered automation
            and modern design.

          </p>


          <div
            className={
              styles.buttons
            }
          >

            <a
              href="#ios"
              onClick={
                handleAndroidDownload
              }
            >
               Android APK
            </a>


            <a
              href="#android"
              className={
                styles.ou
              }
              onClick={
                handleIosDownload 
              }
            >
              IOS APK
            </a>

          </div>

        </div>


        {/* =================================================
            RIGHT GLASS CARD
        ================================================== */}

        <div
          className={
            styles.heroRight
          }
        >

          <div
            className={
              styles.glass
            }
          >

            <h2>
              V 1.0
            </h2>

            <p>
              Download Application
              also available
              in Android and iOS
            </p>

          </div>

        </div>

      </section>


      {/* =================================================
          FEATURE CARDS
      ================================================== */}

      <section
        className={styles.cards}
        id="services"
      >

        {[

          {
            title:
              "Lightning Speed",

            text:
              "Optimized for modern browsers.",
          },

          {
            title:
              "Apple Inspired UI",

            text:
              "Glassmorphism and clean typography.",
          },

          {
            title:
              "AI Ready",

            text:
              "Designed for the future.",
          },

          {
            title:
              "Lightning Speed",

            text:
              "Optimized for modern browsers.",
          },

          {
            title:
              "Apple Inspired UI",

            text:
              "Glassmorphism and clean typography.",
          },

          {
            title:
              "AI Ready",

            text:
              "Designed for the future.",
          },

        ].map(
          (
            card,
            index
          ) => (

            <div
              key={index}
              ref={
                (element) => {

                  cardsRef.current[
                    index
                  ] = element;

                }
              }
              className={
                styles.card
              }
            >

              <h2>
                {card.title}
              </h2>

              <p>
                {card.text}
              </p>

            </div>

          )
        )}

      </section>


      {/* =================================================
          HERO IMAGE SLIDER
      ================================================== */}

      <section
        className={
          styles.heroSlider
        }
      >

        {slides.map(
          (
            slide,
            index
          ) => (

            <div
              key={index}
              className={
                `${styles.slide} ${
                  currentSlide === index
                    ? styles.active
                    : ""
                }`
              }
            >

              <img
                src={slide.image}
                alt={
                  `BroBizee slide ${
                    index + 1
                  }`
                }
              />


              <div
                className={
                  styles.overlay
                }
              />


              <div
                className={
                  styles.sliderContent
                }
              >

                <div
                  className={
                    styles.sliderLeft
                  }
                >

                  <span>
                    {slide.badge}
                  </span>


                  <h1>
                    {slide.title}
                  </h1>


                  {slide.description && (

                    <p>
                      {slide.description}
                    </p>

                  )}


                  {slide.showButtons && (

                    <div
                      className={
                        styles.heroBtns
                      }
                    >

                      <a
                        href="#book"
                        className={
                          styles.btn1
                        }
                        onClick={
                          handleBookService
                        }
                      >
                        Book Service
                      </a>


                      <a
                        href="#explore"
                        className={
                          styles.btn2
                        }
                        onClick={
                          handleExplore
                        }
                      >
                        Explore
                      </a>

                    </div>

                  )}

                </div>


                {slide.showGlassCard && (

                  <div
                    className={
                      styles.sliderRight
                    }
                  >

                    <div
                      className={
                        styles.glassCard
                      }
                    >

                      <img
                        src={
                          slide.image
                        }
                        alt="BroBizee Platform"
                      />

                    </div>

                  </div>

                )}

              </div>

            </div>

          )
        )}


        {/* =================================================
            PREVIOUS BUTTON
        ================================================== */}

        <button
          type="button"
          className={
            styles.prev
          }
          onClick={
            previousSlide
          }
          aria-label="Previous slide"
        >
          &#10094;
        </button>


        {/* =================================================
            NEXT BUTTON
        ================================================== */}

        <button
          type="button"
          className={
            styles.next
          }
          onClick={
            nextSlide
          }
          aria-label="Next slide"
        >
          &#10095;
        </button>


        {/* =================================================
            SLIDE DOTS
        ================================================== */}

        <div
          className={
            styles.sliderDots
          }
        >

          {slides.map(
            (
              _,
              index
            ) => (

              <button
                key={index}
                type="button"
                className={
                  currentSlide === index
                    ? styles.dotActive
                    : styles.dot
                }
                onClick={() =>
                  setCurrentSlide(
                    index
                  )
                }
                aria-label={
                  `Go to slide ${
                    index + 1
                  }`
                }
              />

            )
          )}

        </div>

      </section>


      {/* =================================================
          FOOTER
      ================================================== */}

      <footer
        className={
          styles.footer
        }
        id="contact"
      >

        <div
          className={
            styles.footerContainer
          }
        >

          {/* ABOUT */}

          <div
            className={
              styles.footerAbout
            }
          >

            <img
              src="/images/logo.png"
              alt="BroBizee Logo"
            />

            <h2>
              BroBizee
            </h2>

            <p>
              India's next generation
              digital platform
              providing professional
              home and business
              services.
            </p>

          </div>


          {/* QUICK LINKS */}

          <div
            className={
              styles.footerLinks
            }
          >

            <h3>
              Quick Links
            </h3>

            <a href="#home">
              Home
            </a>

            <a href="#about">
              About
            </a>

            <a href="#services">
              Services
            </a>

            <a href="#contact">
              Contact
            </a>

          </div>


          {/* SERVICES */}

          <div
            className={
              styles.footerServices
            }
          >

            <h3>
              Services
            </h3>

            <a href="#electrician">
              Electrician
            </a>

            <a href="#plumber">
              Plumber
            </a>

            <a href="#cleaning">
              Cleaning
            </a>

            <a href="#carpenter">
              Carpenter
            </a>

          </div>


          {/* CONTACT */}

          <div
            className={
              styles.footerContact
            }
          >

            <h3>
              Contact
            </h3>

            <p>
              📍 Nagpur, India
            </p>

            <p>
              📧 info@brobizee.com
            </p>

            <p>
              📞 +91 95795 30149
            </p>


            <div
              className={
                styles.social
              }
            >

              <a
                href="#facebook"
                aria-label="Facebook"
              >
                f
              </a>

              <a
                href="#instagram"
                aria-label="Instagram"
              >
                ◎
              </a>

              <a
                href="#linkedin"
                aria-label="LinkedIn"
              >
                in
              </a>

              <a
                href="#youtube"
                aria-label="YouTube"
              >
                ▶
              </a>

            </div>

          </div>

        </div>


        <div
          className={
            styles.copyright
          }
        >

          © 2024 BroBizee.
          All Rights Reserved.

        </div>

      </footer>


      {/* =================================================
          ANDROID DOWNLOAD PROGRESS POPUP
      ================================================== */}

      {showDownloadPopup && (

        <div
          className={
            styles.downloadOverlay
          }
        >

          <div
            className={
              styles.downloadPopup
            }
          >

            <div
              className={
                styles.downloadIcon
              }
            >

              {downloadStatus ===
                "downloading" && "⬇️"}

              {downloadStatus ===
                "completed" && "✅"}

              {downloadStatus ===
                "error" && "❌"}

              {downloadStatus ===
                "cancelled" && "⚠️"}

            </div>


            <h2>

              {downloadStatus ===
                "downloading" &&
                "Downloading BroBizee APK"}

              {downloadStatus ===
                "completed" &&
                "Download Complete!"}

              {downloadStatus ===
                "error" &&
                "Download Failed"}

              {downloadStatus ===
                "cancelled" &&
                "Download Cancelled"}

            </h2>


            {downloadStatus ===
              "downloading" && (

              <>

                <div
                  className={
                    styles.progressBarWrapper
                  }
                >

                  <div
                    className={
                      styles.progressBarFill
                    }
                    style={{
                      width:
                        `${downloadProgress}%`,
                    }}
                  />

                </div>


                <p
                  className={
                    styles.progressText
                  }
                >

                  {downloadProgress}%
                  downloaded

                </p>


                <button
                  type="button"
                  className={
                    styles.cancelDownloadBtn
                  }
                  onClick={
                    handleCancelDownload
                  }
                >
                  Cancel
                </button>

              </>

            )}


            {downloadStatus ===
              "completed" && (

              <p
                className={
                  styles.progressText
                }
              >

                BroBizee.apk saved
                successfully 🎉

              </p>

            )}


            {downloadStatus ===
              "error" && (

              <p
                className={
                  styles.progressText
                }
              >

                कृपया दोबारा try करें।

              </p>

            )}


            {downloadStatus ===
              "cancelled" && (

              <p
                className={
                  styles.progressText
                }
              >

                Download रोक दिया गया।

              </p>

            )}

          </div>

        </div>

      )}


      {/* =================================================
          COOKIE CONSENT POPUP
      ================================================== */}

      {showCookiePopup && (

        <div
          className={
            styles.cookieOverlay
          }
        >

          <div
            className={
              styles.cookiePopup
            }
          >

            {/* COOKIE ICON */}

            <div
              className={
                styles.cookieIcon
              }
            >
              🍪
            </div>


            {/* TITLE */}

            <h2>
              We Value Your Privacy
            </h2>


            {/* DESCRIPTION */}

            <p>

              We use cookies and similar
              technologies to keep BroBizee
              secure, remember your preferences,
              understand how our website is used,
              and improve your experience.

            </p>


            {/* DETAILS */}

            <p
              className={
                styles.cookieSmallText
              }
            >

              Necessary cookies are required
              for the website to function.
              Optional cookies are used only
              when you allow them.

            </p>


            {/* =================================================
                CUSTOM SETTINGS
            ================================================== */}

            {showCookieSettings && (

              <div
                className={
                  styles.cookieSettings
                }
              >

                {/* NECESSARY */}

                <div
                  className={
                    styles.cookieSettingRow
                  }
                >

                  <div>

                    <h4>
                      Necessary
                    </h4>

                    <span>
                      Required for basic
                      website functionality.
                    </span>

                  </div>


                  <button
                    type="button"
                    className={
                      `${styles.cookieToggle} ${
                        styles.cookieToggleActive
                      }`
                    }
                    disabled
                  >

                    <span />

                  </button>

                </div>


                {/* PREFERENCES */}

                <div
                  className={
                    styles.cookieSettingRow
                  }
                >

                  <div>

                    <h4>
                      Preferences
                    </h4>

                    <span>
                      Remembers your settings
                      and preferences.
                    </span>

                  </div>


                  <button
                    type="button"
                    className={
                      cookieSettings.preferences
                        ? `${styles.cookieToggle} ${styles.cookieToggleActive}`
                        : styles.cookieToggle
                    }
                    onClick={() =>
                      handleCookieToggle(
                        "preferences"
                      )
                    }
                    aria-label="Toggle preferences cookies"
                  >

                    <span />

                  </button>

                </div>


                {/* ANALYTICS */}

                <div
                  className={
                    styles.cookieSettingRow
                  }
                >

                  <div>

                    <h4>
                      Analytics
                    </h4>

                    <span>
                      Helps us understand
                      website usage.
                    </span>

                  </div>


                  <button
                    type="button"
                    className={
                      cookieSettings.analytics
                        ? `${styles.cookieToggle} ${styles.cookieToggleActive}`
                        : styles.cookieToggle
                    }
                    onClick={() =>
                      handleCookieToggle(
                        "analytics"
                      )
                    }
                    aria-label="Toggle analytics cookies"
                  >

                    <span />

                  </button>

                </div>


                {/* MARKETING */}

                <div
                  className={
                    styles.cookieSettingRow
                  }
                >

                  <div>

                    <h4>
                      Marketing
                    </h4>

                    <span>
                      Used for relevant
                      promotional experiences.
                    </span>

                  </div>


                  <button
                    type="button"
                    className={
                      cookieSettings.marketing
                        ? `${styles.cookieToggle} ${styles.cookieToggleActive}`
                        : styles.cookieToggle
                    }
                    onClick={() =>
                      handleCookieToggle(
                        "marketing"
                      )
                    }
                    aria-label="Toggle marketing cookies"
                  >

                    <span />

                  </button>

                </div>


                {/* SAVE */}

                <button
                  type="button"
                  className={
                    styles.cookieSaveButton
                  }
                  onClick={
                    handleSaveCookieSettings
                  }
                >
                  Save Preferences
                </button>

              </div>

            )}


            {/* =================================================
                MAIN COOKIE BUTTONS
            ================================================== */}

            {!showCookieSettings && (

              <div
                className={
                  styles.cookieActions
                }
              >

                <button
                  type="button"
                  className={
                    styles.cookieReject
                  }
                  onClick={
                    handleRejectOptional
                  }
                >
                  Reject Optional
                </button>


                <button
                  type="button"
                  className={
                    styles.cookieCustomize
                  }
                  onClick={() =>
                    setShowCookieSettings(
                      true
                    )
                  }
                >
                  Customize
                </button>


                <button
                  type="button"
                  className={
                    styles.cookieAccept
                  }
                  onClick={
                    handleAcceptAllCookies
                  }
                >
                  Accept All
                </button>

              </div>

            )}


            {/* PRIVACY TEXT */}

            <div
              className={
                styles.cookieFooterText
              }
            >

              By selecting "Accept All",
              you agree to the use of
              optional cookies as described
              in our privacy policy.

            </div>

          </div>

        </div>

      )}

    </div>

  );

}